<template>
    <div class="poi-categories-settings" ref="settingsPanel">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
                <h5 class="section-title mb-1">
                    {{ __('manage_poi.categories.title') }}
                </h5>
                <p class="text-muted mb-0">
                    {{ __('manage_poi.categories.subtitle') }}
                </p>
            </div>
        </div>

        <div class="bg-main-inner-bg p-3 rounded-3 mb-4" :class="{ 'form-highlight': formHighlight }"
            ref="categoryForm">
            <div class="row g-3 mb-3">
                <div class="col-lg-4">
                    <label class="form-label d-block">
                        {{ __('manage_poi.categories.description') }}
                    </label>
                    <golia-input v-model.trim="form.description" :label="__('manage_poi.categories.description')"
                        :placeholder="__('manage_poi.categories.add_category_placeholder')" />
                </div>
                <div class="col-lg-3">
                    <label class="form-label d-block">
                        {{ __('manage_poi.categories.color') }}
                    </label>
                    <input v-model="form.color" type="color" class="form-control form-control-color" />
                </div>
            </div>
            <div class="">
                <label class="form-label d-block">
                    {{ __('manage_poi.categories.icon') }}
                </label>
                <font-awesome-glyph-picker v-model="form.fa" :modal-id="'poi-category-fa-picker'" />
            </div>

            <div class="form-actions d-flex justify-content-start gap-2 mt-3">
                <golia-button v-if="isEditing" variant="btn-fill gray" :label="__('manage_poi.categories.cancel')"
                    @click="resetForm" />
                <golia-button variant="btn-fill dark-blue"
                    :label="isEditing ? __('manage_poi.categories.update_category') : __('manage_poi.categories.add_category')"
                    @click="submitForm" />
            </div>
        </div>

        <div class="categories-list bg-main-inner-bg p-3 rounded-3">
            <div class="category-row category-row-head">
                <span>{{ __('manage_poi.categories.icon') }}</span>
                <span>{{ __('manage_poi.categories.description') }}</span>
                <span>{{ __('manage_poi.categories.color') }}</span>
                <span>{{ __('manage_poi.categories.scope') }}</span>
                <span class="text-end">{{ __('manage_poi.categories.actions') }}</span>
            </div>

            <div class="category-row" v-for="category in sortedCategories" :key="category.id">
                <span class="category-icon-preview">
                    <span class="glyph" :style="{ color: category.color || '#000000' }"
                        v-html="normalizeGlyph(category.fa) || fallbackGlyph"></span>
                </span>
                <span>{{ category.description }}</span>
                <span>
                    <span class="color-chip" :style="{ backgroundColor: category.color }"></span>
                    {{ category.color }}
                </span>
                <span>{{ category.company_id === 0 ? 'System' : 'Custom' }}</span>
                <span class="actions text-end">
                    <golia-button variant="btn-info btn-sm" icon="fa-fw fa-pencil" @click="openEditForm(category)"
                        :disabled="category.company_id === 0" />
                    <golia-button variant="btn-danger btn-sm" icon="fa-fw fa-trash" @click="openDeleteModal(category)"
                        :disabled="category.company_id === 0" />
                </span>
            </div>
        </div>

        <b-modal id="poi-category-delete-modal" title="Delete category" @hidden="resetDeleteState">
            <div v-if="categoryToDelete">
                <p>
                    Delete category <b>{{ categoryToDelete.description }}</b>.
                </p>
                <p class="text-muted">
                    If there are linked POIs, they will be transferred to the selected category.
                </p>
                <golia-input v-model="deleteTransferTo" type="select" :options="transferOptions"
                    :label="'Transfer POIs to'" />
            </div>

            <template #modal-footer="{ cancel }">
                <golia-button @click="cancel()" :label="'Cancel'" variant="btn-fill gray" />
                <golia-button @click="deleteCategory" :label="'Delete'" variant="btn-fill danger" />
            </template>
        </b-modal>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex'
    import FontAwesomeGlyphPicker from './FontAwesomeGlyphPicker.vue'
    const fallbackGlyph = '&#xf3c5;' // Font Awesome "map-marker-alt" icon
    function normalizeGlyph(value) {
        if (!value) return ''

        const trimmed = String(value).trim()
        if (!trimmed) return ''

        const entityMatch = trimmed.match(/^&#x?([a-f0-9]+);?$/i)
        if (entityMatch) return `&#x${entityMatch[1].toLowerCase()};`

        const hexMatch = trimmed.match(/^[a-f0-9]+$/i)
        if (hexMatch) return `&#x${trimmed.toLowerCase()};`

        return trimmed
    }

    export default {
        name: 'PoiCategoriesSettings',
        components: {
            FontAwesomeGlyphPicker
        },
        data() {
            return {
                loading: false,
                form: {
                    id: null,
                    description: '',
                    color: '#1D5F9F',
                    fa: ''
                },
                categoryToDelete: null,
                deleteTransferTo: null,
                fallbackGlyph,
                formHighlight: false,
                highlightTimeout: null
            }
        },
        computed: {
            ...mapState({
                categories: (state) => state.pois.poiCategories
            }),
            sortedCategories() {
                return [...this.categories].sort((a, b) => {
                    return (b.company_id || 0) - (a.company_id || 0)
                })
            },
            isEditing() {
                return !!this.form.id
            },
            transferOptions() {
                if (!this.categoryToDelete) return []

                return this.categories
                    .filter((category) => category.id !== this.categoryToDelete.id)
                    .map((category) => ({
                        value: category.id,
                        text: category.description
                    }))
            }
        },
        methods: {
            ...mapActions({
                loadPoiCategories: 'loadPoiCategories'
            }),
            normalizeGlyph,
            async loadCategories() {
                this.loading = true

                try {
                    await this.loadPoiCategories({ vueInstance: this })
                } finally {
                    this.loading = false
                }
            },
            openEditForm(category) {
                if (category.company_id === 0) return

                this.form = {
                    id: category.id,
                    description: category.description || '',
                    color: category.color || '#1D5F9F',
                    fa: normalizeGlyph(category.fa)
                }
                this.$nextTick(() => {
                    this.scrollPanelToTop()
                    this.triggerFormHighlight()
                })
            },
            resetForm() {
                this.form = {
                    id: null,
                    description: '',
                    color: '#1D5F9F',
                    fa: ''
                }
            },
            validateForm() {
                if (!this.form.description.trim()) {
                    this.$snotify.error('Description is required.', { position: 'centerTop' })
                    return false
                }

                if (!/^#([0-9a-f]{6})$/i.test(this.form.color)) {
                    this.$snotify.error('Color must be a valid hexadecimal value.', {
                        position: 'centerTop'
                    })
                    return false
                }

                return true
            },
            async submitForm() {
                if (!this.validateForm()) return

                const payload = {
                    id: this.form.id,
                    description: this.form.description.trim(),
                    color: this.form.color,
                    fa: normalizeGlyph(this.form.fa) || null
                }

                const method = this.isEditing ? 'PUT' : 'POST'
                const url = this.isEditing
                    ? `${this.$env.GOLIA_API}/poi/categories/${this.form.id}`
                    : this.$env.GOLIA_API + '/poi/categories'

                try {
                    const res = await this.$fetch(url, {
                        method,
                        body: JSON.stringify(payload)
                    })
                    const json = await res.json()

                    if (json.errors) {
                        this.$snotify.error('Unable to save category.', { position: 'centerTop' })
                        return
                    }

                    this.$snotify.success(
                        this.isEditing ? 'Category updated.' : 'Category created.',
                        { position: 'centerTop' }
                    )
                    this.resetForm()
                    await this.loadCategories()
                    this.$store.dispatch('loadPois', { vueInstance: this })
                } catch (e) {
                    this.$snotify.error('Unable to save category.', { position: 'centerTop' })
                }
            },
            openDeleteModal(category) {
                if (category.company_id === 0) return

                this.categoryToDelete = category
                this.deleteTransferTo = this.transferOptions[0]?.value || null
                this.$bvModal.show('poi-category-delete-modal')
            },
            resetDeleteState() {
                this.categoryToDelete = null
                this.deleteTransferTo = null
            },
            async deleteCategory() {
                if (!this.categoryToDelete) return

                if (!this.deleteTransferTo) {
                    this.$snotify.error('Select a category to transfer POIs to.', {
                        position: 'centerTop'
                    })
                    return
                }

                try {
                    const res = await this.$fetch(
                        `${this.$env.GOLIA_API}/poi/categories/${this.categoryToDelete.id}?transfer_to=${this.deleteTransferTo}`,
                        {
                            method: 'DELETE'
                        }
                    )
                    const json = await res.json()

                    if (json.errors) {
                        this.$snotify.error('Unable to delete category.', { position: 'centerTop' })
                        return
                    }

                    this.$bvModal.hide('poi-category-delete-modal')
                    this.$snotify.success('Category deleted.', { position: 'centerTop' })
                    await this.loadCategories()
                    this.$store.dispatch('loadPois', { vueInstance: this })
                } catch (e) {
                    this.$snotify.error('Unable to delete category.', { position: 'centerTop' })
                }
            },
            scrollPanelToTop() {
                const panel = this.$refs.settingsPanel
                if (!panel) return

                const scrollContainer = panel.closest('.custom-scrollbar') || panel.parentElement || panel

                if (typeof scrollContainer.scrollTo === 'function') {
                    scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
                    return
                }

                scrollContainer.scrollTop = 0
            },
            triggerFormHighlight() {
                this.formHighlight = false

                requestAnimationFrame(() => {
                    this.formHighlight = true

                    clearTimeout(this.highlightTimeout)
                    this.highlightTimeout = setTimeout(() => {
                        this.formHighlight = false
                    }, 1600)
                })
            }
        },
        created() {
            this.loadCategories()
        },
        beforeDestroy() {
            clearTimeout(this.highlightTimeout)
        }
    }
</script>

<style scoped lang="scss">
    // Let the table scroll horizontally rather than push the page when the
    // columns don't fit (narrow / mobile viewports).
    .categories-list {
        overflow-x: auto;
    }

    .category-row {
        display: grid;
        // icon / scope / actions size to their content; description and color
        // share the rest. min-width keeps the columns usable and triggers the
        // horizontal scroll above once the viewport is narrower than that.
        grid-template-columns: auto 2fr 1fr auto auto;
        gap: 12px;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px solid #e3e8ee;
        min-width: 30rem;
    }

    // Clip cell text so a long description / color value can't bleed over the
    // neighbouring column.
    .category-row > span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .category-row-head {
        font-size: 12px;
        text-transform: uppercase;
        color: #6b7785;
        padding-top: 0;
    }

    .category-icon-preview .glyph {
        font-family: 'Font Awesome Kit', 'FontAwesome', 'Font Awesome 6 Pro';
        font-size: 22px;
        line-height: 1;
    }

    .color-chip {
        width: 16px;
        height: 16px;
        border-radius: 999px;
        display: inline-block;
        vertical-align: middle;
        margin-right: 8px;
        border: 1px solid rgba(0, 0, 0, 0.08);
    }

    .actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
    }

    .poi-categories-settings {
        margin-top: 20px;
    }

    // On mobile the two form buttons don't fit side by side, so stack them.
    .geofleet-mobile .form-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .form-highlight {
        animation: form-highlight-fade 1.6s ease-out;
    }

    @keyframes form-highlight-fade {
        0% {
            background-color: rgba(29, 95, 159, 0.16);
            box-shadow: 0 0 0 2px rgba(29, 95, 159, 0.22);
        }

        100% {
            background-color: transparent;
            box-shadow: 0 0 0 2px rgba(29, 95, 159, 0);
        }
    }
</style>
